// region import React
import React from 'react'
import * as d3 from 'd3'
import featureData from 'components/display/data/featuredata.js'
import './styles.scss'

class RadarGraph extends React.Component {
  static featureData = featureData

  /** D3 **/
  static windowWidth = window.innerWidth
  static isMobile = RadarGraph.windowWidth < 720
  static svgWidth = 680
  static svgHeight = 480
  static margin = { top: 20, right: 0, bottom: 20, left: 0 }
  static groupWidth =
    RadarGraph.svgWidth - RadarGraph.margin.left - RadarGraph.margin.right
  static groupHeight =
    RadarGraph.svgHeight - RadarGraph.margin.top - RadarGraph.margin.bottom
  static radius = Math.min(RadarGraph.groupWidth, RadarGraph.groupHeight) / 2
  static features = [
    'Popularité',
    'Gaieté',
    'Énergie',
    'Parlé/chanté',
    'Sons acoustiques',
    'En live ',
    'Dansabilité',
    'Durée'
  ]

  // Static scales for gridlines
  static radialScale = d3
    .scaleLinear()
    .domain([0, 1.05])
    .range([0, RadarGraph.radius])

  // Static line
  static gridLine = d3
    .lineRadial()
    .angle((d, i) => (Math.PI / 4) * i - Math.PI / 8)
    .radius(function(d) {
      return RadarGraph.radialScale(d.y)
    })
    .curve(d3.curveLinearClosed)

  static gridDataInner = d3.range(8).map(function(d, i) {
    // 8 data points
    return { d: i, y: 0.33 }
  })
  static gridDataOuter = d3.range(8).map(function(d, i) {
    // 8 data points
    return { d: i, y: 0.66 }
  })
  static gridDataBackground = d3.range(8).map(function(d, i) {
    // 8 data points
    return { d: i, y: 1 }
  })

  static returnAxis = function(generationValues, userValue, avgValue) {
    let arr = Object.keys(generationValues).map(function(key) {
      return generationValues[key].value
    })
    if (userValue && avgValue) {
      arr.push(userValue, avgValue)
    }
    let min = Math.min.apply(null, arr)
    let max = Math.max.apply(null, arr)
    let yDomainLower = min - (max - min) / 2
    let yDdomainUpper = max + (max - min) / 2

    return d3
      .scaleLinear()
      .domain([yDomainLower < 0 ? 0 : yDomainLower, yDdomainUpper])
      .range([0, RadarGraph.radius])
  }

  render() {
    const featureProfile = this.props.appData.user.featureProfile
    const runningAverage = this.props.appData.runningAverage

    // Dynamic scale
    RadarGraph.radialScales = []
    for (let i = 0, len = RadarGraph.featureData.length; i < len; i++) {
      RadarGraph.radialScales[i] = RadarGraph.returnAxis(
        RadarGraph.featureData[i].values,
        featureProfile[RadarGraph.featureData[i].key],
        runningAverage[RadarGraph.featureData[i].key]
      )
    }

    // Dynamic line (user)
    RadarGraph.line = d3
      .lineRadial()
      .angle((d, i) => (Math.PI / 4) * i - Math.PI / 8)
      .radius(function(d, i) {
        return RadarGraph.radialScales[i](featureProfile[d.key])
      })
      .curve(d3.curveLinearClosed)

    // Dynamic line (avg)
    RadarGraph.lineAvg = d3
      .lineRadial()
      .angle((d, i) => (Math.PI / 4) * i - Math.PI / 8)
      .radius(function(d, i) {
        return RadarGraph.radialScales[i](runningAverage[d.key])
      })
      .curve(d3.curveLinearClosed)

    // Dynamic circles
    RadarGraph.renderCircles = function(dataset) {
      return dataset.map((d, i) => (
        <>
          <circle
            className='circle'
            cx={
              RadarGraph.radialScales[i](featureProfile[d.key]) *
              Math.sin((Math.PI / 4) * i - Math.PI / 8)
            }
            cy={
              -RadarGraph.radialScales[i](featureProfile[d.key]) *
              Math.cos((Math.PI / 4) * i - Math.PI / 8)
            }
            r={4}
            style={{ fill: '#54af71' }}
            key={Math.random() * 1}
          />
          <circle
            className='circle'
            cx={
              RadarGraph.radialScales[i](runningAverage[d.key]) *
              Math.sin((Math.PI / 4) * i - Math.PI / 8)
            }
            cy={
              -RadarGraph.radialScales[i](runningAverage[d.key]) *
              Math.cos((Math.PI / 4) * i - Math.PI / 8)
            }
            r={4}
            style={{ fill: '#007abf' }}
            key={Math.random() * 1}
          />
        </>
      ))
    }

    return (
      <>
        <svg
          className='radarGraph'
          width='100%'
          viewBox={`0 0 ${RadarGraph.svgWidth} ${RadarGraph.svgHeight}`}
        >
          {/* Background */}
          <rect height='100%' width='100%' fill='#353535' />

          {/* Main Group */}
          <g
            className='svgGroup'
            transform={`translate(${RadarGraph.groupWidth / 2 +
              RadarGraph.margin.left}, ${RadarGraph.groupHeight / 2 +
              RadarGraph.margin.top})`}
          >
            {/* Gridlines */}
            <path
              className='grid-line'
              d={RadarGraph.gridLine(RadarGraph.gridDataBackground)}
              ref={node =>
                d3
                  .select(node)
                  .attr('fill', '#2B2B2A')
                  .attr('stroke', 'none')
              }
            />
            <path
              className='grid-line'
              d={RadarGraph.gridLine(RadarGraph.gridDataInner)}
              ref={node =>
                d3
                  .select(node)
                  .attr('fill', 'none')
                  .attr('stroke', '#4d4d4d')
              }
            />
            <path
              className='grid-line'
              d={RadarGraph.gridLine(RadarGraph.gridDataOuter)}
              ref={node =>
                d3
                  .select(node)
                  .attr('fill', 'none')
                  .attr('stroke', '#4d4d4d')
              }
            />

            {/* Axis */}
            <g
              className='angular-axis'
              ref={node =>
                d3
                  .select(node)
                  .selectAll('g')
                  .data(d3.range(-22.5, 360, 45))
                  .enter()
                  .append('g')
                  .attr('transform', function(d) {
                    return 'rotate(' + -d + ')'
                  })
                  .append('line')
                  .attr('x2', RadarGraph.radius)
                  .attr('stroke', '#bebebe')
              }
            />

            {/* Axis Text */}
            <g
              className='angular-axis-text'
              ref={node =>
                d3
                  .select(node)
                  .selectAll('g')
                  .data(RadarGraph.features)
                  .enter()
                  .append('text')
                  .text(function(d) {
                    return d
                  })
                  .attr('x', function(d, i) {
                    return (
                      RadarGraph.radius *
                      Math.sin((Math.PI / 4) * i - Math.PI / 8)
                    )
                  })
                  .attr('y', function(d, i) {
                    return (
                      -RadarGraph.radius *
                      Math.cos((Math.PI / 4) * i - Math.PI / 8)
                    )
                  })
                  .style('text-anchor', function(d, i) {
                    return i < 1 || i > 4 ? 'end' : null
                  })
                  .attr('font-size', RadarGraph.isMobile ? '24.5px' : '23px')
                  .attr('transform', function(d, i) {
                    switch (i) {
                      case 0:
                        return 'translate(-10, -10)'
                      case 1:
                        return 'translate(10, -10)'
                      case 2:
                        return 'translate(5, -5)'
                      case 3:
                        return 'translate(0, 25)'
                      case 4:
                        return 'translate(5, 20)'
                      case 5:
                        return 'translate(-5, 20)'
                      case 6:
                        return 'translate(0, 25)'
                      case 7:
                        return 'translate(-5, -5)'
                      default:
                        return null
                    }
                  })
                  .attr('class', 'radar-axis-text')
              }
            />

            {/* Line/Fill (avg) */}
            <path
              className='line line-avg'
              d={RadarGraph.lineAvg(RadarGraph.featureData)}
            />

            {/* Line */}
            <path
              className='line line-user'
              d={RadarGraph.line(RadarGraph.featureData)}
            />

            {/* Dots */}
            <g className='dots'>
              {RadarGraph.renderCircles(RadarGraph.featureData)}
            </g>
          </g>
        </svg>
      </>
    )
  }
}

export default RadarGraph
